import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Download, Printer } from 'lucide-react';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { Button } from 'components/common/Button';
import { Card } from 'components/common/Card';
import { EmptyState, LoadingScreen } from 'components/common/Spinner';
import { AlertService } from 'services/alerts/alert.service';
import { StoreDataService } from 'services/data/store-data.service';
import { Alert } from 'dtos/alerts/alert.dto';
import { summarizeAudit } from 'utils/audit';
import { formatCurrency, formatDate } from 'utils/formatters';

async function loadOpenAlerts(orgId: string, storeId: string): Promise<Alert[]> {
  const all: Alert[] = [];
  for (const status of ['NEW', 'ACKNOWLEDGED'] as const) {
    for (let page = 1; page <= 20; page += 1) {
      const result = await AlertService.list(orgId, storeId, status, page, 100);
      all.push(...result.data);
      if (all.filter(alert => alert.status === status).length >= result.meta.total || !result.data.length) break;
    }
  }
  return all;
}

function Figure({ label, value, hint, tone = 'default' }: { label: string; value: string; hint: string; tone?: 'default' | 'warn' }) {
  return (
    <div className={`rounded-lg border p-4 ${tone === 'warn' ? 'border-orange-200 bg-orange-50' : 'border-gray-200'}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      <p className="mt-1 text-xs text-gray-600">{hint}</p>
    </div>
  );
}

// The Margin Audit: a one-page, printable findings report for the owner. It is
// the core of the free-audit sales motion, so every number is traceable to an
// alert the owner can open in the app.
export default function AuditReportPage() {
  const { org, currentStore } = useStore();
  const navigate = useNavigate();
  const [downloadError, setDownloadError] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const { data, loading, error, refetch } = useApi(
    async () => {
      const [alerts, changes, health] = await Promise.all([
        loadOpenAlerts(org?.id ?? '', currentStore?.id ?? ''),
        StoreDataService.priceChanges(org?.id ?? '', currentStore?.id ?? ''),
        StoreDataService.health(org?.id ?? '', currentStore?.id ?? ''),
      ]);
      return { alerts, changes, health };
    },
    [org?.id, currentStore?.id]
  );

  if (loading && !data) return <LoadingScreen />;
  if (error || !data) return <EmptyState icon={<AlertTriangle size={40} />} title="We couldn't build the audit" message={error ?? undefined} action={<Button variant="secondary" onClick={refetch}>Try again</Button>} />;

  const summary = summarizeAudit(data.alerts, today);
  const { changes, health } = data;
  const download = async () => {
    setDownloadError('');
    try { await StoreDataService.downloadPriceChanges(org?.id ?? '', currentStore?.id ?? ''); }
    catch (err: any) { setDownloadError(err?.message || 'Download failed.'); }
  };

  if (!health.ready_for_alerts) {
    return <EmptyState icon={<AlertTriangle size={40} />} title="Not enough data for an audit yet"
      message="The audit needs the store's price list, recent item sales and supplier costs."
      action={<Button onClick={() => navigate('/data')}>Set up store data</Button>} />;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 print:max-w-none">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Pellvr Margin Audit</p>
          <h1 className="text-2xl font-bold text-gray-900">{currentStore?.name}</h1>
          <p className="text-sm text-gray-500">Prepared {formatDate(today)} · sales through {health.sales.last_business_date ? formatDate(health.sales.last_business_date) : '—'} · {health.costs.sold_products_with_cost_pct ?? 0}% of items sold have a known cost</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="secondary" size="sm" onClick={() => void download()}><Download size={14} /> Price-change sheet</Button>
          <Button size="sm" onClick={() => window.print()}><Printer size={14} /> Print / save PDF</Button>
        </div>
      </div>
      {downloadError && <p className="text-sm text-red-600 print:hidden">{downloadError}</p>}

      <div className="grid gap-4 sm:grid-cols-3">
        <Figure tone="warn" label="Cost increases not passed on" value={`${formatCurrency(summary.costIncreaseMonthly)}/mo`}
          hint={`${summary.costIncreaseCount} item${summary.costIncreaseCount === 1 ? '' : 's'} where your supplier cost went up and the shelf price didn't.`} />
        <Figure label="Already lost on those items" value={formatCurrency(summary.alreadyLost)}
          hint={summary.oldestIncreaseDate ? `Since the first increase on ${formatDate(summary.oldestIncreaseDate)}, at your current sales.` : 'No dated cost increases found.'} />
        <Figure label="Margin opportunities" value={`${formatCurrency(summary.marginMonthly)}/mo`}
          hint={`${summary.marginCount} item${summary.marginCount === 1 ? '' : 's'} earning well below similar items in your store. Your call whether to raise them.`} />
      </div>

      <Card padding={false}>
        <div className="border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Prices to change ({changes.length})</h2>
          <p className="text-xs text-gray-500">Sorted by monthly profit. Key these into your POS, or hand the price-change sheet to whoever maintains your price book.</p>
        </div>
        {changes.length === 0 ? <p className="px-5 py-8 text-center text-sm text-gray-500">No price changes suggested right now.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr><th className="px-4 py-2">Item</th><th className="px-4 py-2">UPC</th><th className="px-4 py-2 text-right">Now</th><th className="px-4 py-2 text-right">Change to</th><th className="px-4 py-2">Why</th><th className="px-4 py-2 text-right">Per month</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {changes.map(change => (
                  <tr key={change.product_id}>
                    <td className="px-4 py-2 font-medium text-gray-900">{change.item}</td>
                    <td className="px-4 py-2 text-gray-500">{change.upc || change.sku || '—'}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(change.current_price, 2)}</td>
                    <td className="px-4 py-2 text-right font-semibold">{formatCurrency(change.new_price, 2)}</td>
                    <td className="px-4 py-2 text-gray-600">{change.reason}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(change.est_monthly_gain)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="text-sm font-semibold text-gray-900">How we worked this out</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
          <li>Cost increase per month = (new cost − old cost) × units you sold in the last 30 days. Every number comes from your own invoices, cost files and sales.</li>
          <li>Suggested prices only ever go up, keep at least your old profit per item, and round to a normal price ending. Check state minimum-price rules for tobacco before changing those prices.</li>
          <li>Margin opportunities compare an item with similar items in your own store (multipacks with multipacks). They are suggestions, not losses.</li>
          <li>{health.costs.sold_products_with_cost_pct !== null && health.costs.sold_products_with_cost_pct < 80 ? `Only ${health.costs.sold_products_with_cost_pct}% of the items you sold have a cost. Invoices from your other suppliers would likely find more.` : 'Most of the items you sold have a known cost, so this list is close to complete.'}</li>
        </ul>
      </Card>

      <Card className="print:hidden">
        <h2 className="text-sm font-semibold text-gray-900">Keep this from happening again</h2>
        <p className="mt-1 text-sm text-gray-600">Pellvr checks every item against your newest costs every night and tells you the day a supplier raises a price: {formatCurrency(49)}/store/month, or {formatCurrency(39)} billed annually. If it doesn't find at least your fee in your first paid month, we refund that month.</p>
        <Button className="mt-3" size="sm" onClick={() => navigate('/settings/billing')}>Keep Pellvr watching</Button>
      </Card>
    </div>
  );
}
